// 由現場量測的「點與點之間的水平距離」換算 GRP 的局部座標。
//
// 現地如果沒有 RTK 或全站儀，常見的作法是拿捲尺量各點之間的距離。距離本身
// 不能直接當 GRP 用 —— 正射轉換需要每一點的 X/Y —— 所以這裡用距離把所有點
// 擺進同一個平面座標系：
//
//   1. 原點 A 放在 (0, 0)，方向點 B 放在 +X 軸上 (|AB|, 0)
//   2. 其餘的點依序用「到兩個已定位點的距離」做三邊交會；量到三個以上時，
//      用多出來的距離挑對兩個交點中的哪一個
//   3. 用全部的量測做一次最小平方平差（高斯牛頓），把量測誤差平均分攤掉
//   4. 判斷左右方向：只有距離的話，整組座標和它的鏡像同樣成立。這裡用影像
//      上已指認的位置決定 —— 攝影機一定在水面上方往下看，所以實際座標的
//      三角形方向必定與影像（py 向下）上的方向相反
//
// 純函式、不依賴 React，方便單獨測試。

const EPS = 1e-9;

const fin = (v) => typeof v === "number" && Number.isFinite(v);

/** 三角形 o→a→b 的有向面積（×2）。正值為逆時針。 */
function cross(o, a, b) {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/**
 * 兩圓交點。回傳 { left, right, h }：left 在 p1→p2 的左側。
 * 量測誤差讓兩圓不相交時，h 取 0（兩圓最接近的那一點），不當成失敗 ——
 * 捲尺量出來的距離本來就有幾公分誤差，平差會再把它修正。
 */
function intersect(p1, d1, p2, d2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const D = Math.hypot(dx, dy);
  if (D < EPS) return null;
  const a = (d1 * d1 - d2 * d2 + D * D) / (2 * D);
  const h2 = d1 * d1 - a * a;
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  const mx = p1.x + (a * dx) / D;
  const my = p1.y + (a * dy) / D;
  return {
    left: { x: mx - (h * dy) / D, y: my + (h * dx) / D },
    right: { x: mx + (h * dy) / D, y: my - (h * dx) / D },
    h,
  };
}

/** 高斯消去法解 A·x = b（部分樞軸）。奇異時回傳 null。 */
function solveLinear(A, b) {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(M[row][col]) > Math.abs(M[pivot][col])) pivot = row;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) return null;
    [M[col], M[pivot]] = [M[pivot], M[col]];
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = M[row][col] / M[col][col];
      for (let k = col; k <= n; k += 1) M[row][k] -= factor * M[col][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

function sumSquares(pos, meas) {
  return meas.reduce((sum, m) => {
    const r = distance(pos.get(m.i), pos.get(m.j)) - m.d;
    return sum + r * r;
  }, 0);
}

/**
 * 最小平方平差。原點固定、方向點只能沿 X 軸移動，其餘點的 x、y 都是未知數。
 * Levenberg–Marquardt：阻尼讓初始位置稍差（例如兩圓不相交被截成 h = 0）時
 * 仍能收斂。
 */
function adjust(pos, meas, origin, axis, iterations = 50) {
  const vars = [];
  pos.forEach((_, k) => {
    if (k === origin) return;
    vars.push({ k, c: "x" });
    if (k !== axis) vars.push({ k, c: "y" });
  });
  if (!vars.length) return pos;
  const index = new Map(vars.map((v, n) => [`${v.k}:${v.c}`, n]));
  const n = vars.length;

  let current = new Map([...pos].map(([k, p]) => [k, { ...p }]));
  let best = sumSquares(current, meas);
  let lambda = 1e-3;

  for (let step = 0; step < iterations; step += 1) {
    const JtJ = Array.from({ length: n }, () => new Array(n).fill(0));
    const Jtr = new Array(n).fill(0);

    meas.forEach((m) => {
      const a = current.get(m.i);
      const b = current.get(m.j);
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const L = Math.hypot(dx, dy);
      if (L < EPS) return;
      const r = L - m.d;
      const ux = dx / L;
      const uy = dy / L;
      const grads = [
        [index.get(`${m.i}:x`), ux], [index.get(`${m.i}:y`), uy],
        [index.get(`${m.j}:x`), -ux], [index.get(`${m.j}:y`), -uy],
      ].filter(([i]) => i !== undefined);
      grads.forEach(([i, gi]) => {
        Jtr[i] += gi * r;
        grads.forEach(([j, gj]) => { JtJ[i][j] += gi * gj; });
      });
    });

    const damped = JtJ.map((row, i) =>
      row.map((v, j) => (i === j ? v * (1 + lambda) + 1e-12 : v)));
    const delta = solveLinear(damped, Jtr.map((v) => -v));
    if (!delta) break;

    const next = new Map([...current].map(([k, p]) => [k, { ...p }]));
    vars.forEach((v, i) => { next.get(v.k)[v.c] += delta[i]; });
    const cost = sumSquares(next, meas);

    if (cost < best) {
      const gain = best - cost;
      current = next;
      best = cost;
      lambda = Math.max(lambda / 10, 1e-9);
      if (gain < 1e-14) break;
    } else {
      lambda *= 10;
      if (lambda > 1e8) break;
    }
  }
  return current;
}

/** 把點 p 對 a→b 這條線做鏡射。 */
function reflect(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const L2 = dx * dx + dy * dy;
  if (L2 < EPS) return p;
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2;
  const fx = a.x + t * dx;
  const fy = a.y + t * dy;
  return { x: 2 * fx - p.x, y: 2 * fy - p.y };
}

/**
 * 換算座標。
 *
 * input:
 *   origin, axis    GRP 索引（0 起算）：原點、+X 方向點
 *   x0, y0          原點的座標，預設 (0, 0)
 *   distances       [{ i, j, d }]，i、j 為 GRP 索引，d 為水平距離（公尺）
 *   pixels          [{ px, py } | null]，以 GRP 索引對應；用來判斷左右方向
 *
 * output:
 *   coords          { [索引]: { X, Y } }，只含能定位的點
 *   residuals       [{ i, j, d, calc, res }]，每一筆量測的平差後差值
 *   rms             差值的均方根（公尺）
 *   redundancy      多餘量測數；0 代表量錯了也看不出來
 *   unplaced        量測不足、無法定位的點
 *   orientation     "image"（由影像判斷）| "default"（無法判斷，採預設）
 *   warnings        給使用者看的提醒
 *   error           無法計算的原因
 */
export function solveSurvey({
  origin, axis, x0 = 0, y0 = 0, distances = [], pixels = [],
}) {
  const out = {
    coords: {}, residuals: [], rms: null, redundancy: null, unplaced: [],
    orientation: null, warnings: [], error: null,
  };

  if (!Number.isInteger(origin) || !Number.isInteger(axis)) {
    out.error = "請先選擇原點與方向點";
    return out;
  }
  if (origin === axis) {
    out.error = "原點與方向點不能是同一點";
    return out;
  }

  const meas = (distances || []).filter((m) =>
    m && Number.isInteger(m.i) && Number.isInteger(m.j) && m.i !== m.j
    && fin(m.d) && m.d > 0);
  if (!meas.length) {
    out.error = "尚未輸入任何距離";
    return out;
  }

  // 相鄰表：同一對點量了不只一次就取平均，當作初始定位用
  const neighbors = new Map();
  const link = (a, b, d) => {
    if (!neighbors.has(a)) neighbors.set(a, new Map());
    const list = neighbors.get(a).get(b) || [];
    list.push(d);
    neighbors.get(a).set(b, list);
  };
  meas.forEach((m) => { link(m.i, m.j, m.d); link(m.j, m.i, m.d); });
  const mean = (list) => list.reduce((s, v) => s + v, 0) / list.length;
  const degree = (k) => (neighbors.get(k) ? neighbors.get(k).size : 0);

  const ab = neighbors.get(origin)?.get(axis);
  if (!ab) {
    out.error = `需要原點 #${origin + 1} 到方向點 #${axis + 1} 的距離`;
    return out;
  }

  // -- 1. 依序定位 -------------------------------------------------------
  const pos = new Map([[origin, { x: 0, y: 0 }], [axis, { x: mean(ab), y: 0 }]]);
  const skipped = new Set();
  for (;;) {
    let best = null;
    neighbors.forEach((map, p) => {
      if (pos.has(p) || skipped.has(p)) return;
      const refs = [...map].filter(([k]) => pos.has(k))
        .map(([k, list]) => ({ k, d: mean(list) }));
      if (refs.length >= 2 && (!best || refs.length > best.refs.length)) {
        best = { p, refs };
      }
    });
    if (!best) break;

    // 兩個參考點與待定點越接近直角，交點越穩定；以交點離基線的距離 h 評分
    let pick = null;
    for (let a = 0; a < best.refs.length; a += 1) {
      for (let b = a + 1; b < best.refs.length; b += 1) {
        const r1 = best.refs[a];
        const r2 = best.refs[b];
        const hit = intersect(pos.get(r1.k), r1.d, pos.get(r2.k), r2.d);
        if (hit && (!pick || hit.h > pick.h)) pick = hit;
      }
    }
    if (!pick) {
      skipped.add(best.p);
      continue;
    }

    let chosen = pick.left;
    if (best.refs.length >= 3) {
      const misfit = (c) => best.refs.reduce((s, r) => {
        const e = distance(c, pos.get(r.k)) - r.d;
        return s + e * e;
      }, 0);
      chosen = misfit(pick.left) <= misfit(pick.right) ? pick.left : pick.right;
    }
    pos.set(best.p, chosen);
  }

  neighbors.forEach((_, k) => { if (!pos.has(k)) out.unplaced.push(k); });
  out.unplaced.sort((a, b) => a - b);
  if (out.unplaced.length) {
    out.warnings.push(
      `第 ${out.unplaced.map((k) => k + 1).join("、")} 點無法定位：`
      + "每一點至少要量到兩個已經定位的點");
  }

  // -- 2. 平差 -----------------------------------------------------------
  const usable = meas.filter((m) => pos.has(m.i) && pos.has(m.j));
  let adjusted = adjust(pos, usable, origin, axis);

  // -- 3. 左右方向 -------------------------------------------------------
  const pixel = (k) => {
    const p = pixels && pixels[k];
    return p && fin(p.px) && fin(p.py) ? { x: p.px, y: p.py } : null;
  };
  // 只連到兩個點的自由點，本身就能對那兩點的連線鏡射，不能拿來判斷整體方向
  const loose = (k) => k !== origin && k !== axis && degree(k) === 2;

  // 回傳加權票數：正值 = 與影像方向一致（相反號），負值 = 需要鏡射
  const vote = (a, b, c) => {
    const ia = pixel(a);
    const ib = pixel(b);
    const ic = pixel(c);
    if (!ia || !ib || !ic) return 0;
    const world = cross(adjusted.get(a), adjusted.get(b), adjusted.get(c));
    const image = cross(ia, ib, ic);
    if (Math.abs(world) < 1e-6 || Math.abs(image) < 1) return 0;
    return world * image < 0 ? Math.abs(world) : -Math.abs(world);
  };

  const placed = [...adjusted.keys()];
  const tally = (allowLoose) => {
    let total = 0;
    let count = 0;
    for (let a = 0; a < placed.length; a += 1) {
      for (let b = a + 1; b < placed.length; b += 1) {
        for (let c = b + 1; c < placed.length; c += 1) {
          const tri = [placed[a], placed[b], placed[c]];
          if (!allowLoose && tri.some(loose)) continue;
          const v = vote(...tri);
          if (v !== 0) { total += v; count += 1; }
        }
      }
    }
    return { total, count };
  };

  let votes = tally(false);
  if (!votes.count) votes = tally(true);
  if (votes.count) {
    out.orientation = "image";
    if (votes.total < 0) {
      adjusted = new Map([...adjusted].map(([k, p]) => [k, { x: p.x, y: -p.y }]));
    }
  } else {
    out.orientation = "default";
    out.warnings.push(
      "影像上已指認的點不足三個，無法判斷左右方向，暫以「站在原點看向方向點，"
      + "+Y 在左手邊」計算。指認更多點後會自動判斷");
  }

  // 只量了兩段距離的點：個別對它的兩個鄰點連線檢查方向
  placed.filter(loose).forEach((k) => {
    const [n1, n2] = [...neighbors.get(k).keys()];
    const v = vote(n1, n2, k);
    if (v < 0) {
      adjusted.set(k, reflect(adjusted.get(k), adjusted.get(n1), adjusted.get(n2)));
    } else if (v === 0) {
      out.warnings.push(
        `第 ${k + 1} 點只量了兩段距離且未在影像上指認，位置可能左右顛倒；`
        + "建議再量一段到其他點");
    }
  });

  // -- 4. 結果 -----------------------------------------------------------
  const round = (v) => Math.round(v * 1000) / 1000;
  adjusted.forEach((p, k) => {
    out.coords[k] = { X: round(x0 + p.x), Y: round(y0 + p.y) };
  });

  out.residuals = usable.map((m) => {
    const calc = distance(adjusted.get(m.i), adjusted.get(m.j));
    return { i: m.i, j: m.j, d: m.d, calc: round(calc), res: round(calc - m.d) };
  });
  if (out.residuals.length) {
    out.rms = Math.sqrt(out.residuals.reduce((s, r) => s + r.res * r.res, 0)
      / out.residuals.length);
  }

  // 未知數：方向點 1 個（x），其餘自由點各 2 個
  out.redundancy = usable.length - (1 + 2 * Math.max(0, adjusted.size - 2));
  if (out.redundancy <= 0 && adjusted.size > 2) {
    out.warnings.push(
      "沒有多餘的量測，量錯了也看不出來；建議每一點至少量到三個點");
  }

  return out;
}